import { z } from "zod/v4";

// Narrow types for the enum-like text columns on the amp_* board tables.
// The database checks only the workflow status categories, so these lists are the source of truth.
// Reads return string, so cast at the read boundary.

export const AMP_PROJECT_STATUSES = ["draft", "in_progress", "done"] as const;
export type AmpProjectStatus = (typeof AMP_PROJECT_STATUSES)[number];

export function isAmpProjectStatus(value: string): value is AmpProjectStatus {
  return (AMP_PROJECT_STATUSES as readonly string[]).includes(value);
}

export const AMP_PROJECT_OPENED_BY = ["user", "agent"] as const;
export type AmpProjectOpenedBy = (typeof AMP_PROJECT_OPENED_BY)[number];

export const AMP_PROJECT_EXTERNAL_REF_TYPES = ["asana", "workfront"] as const;
export type AmpProjectExternalRefType =
  (typeof AMP_PROJECT_EXTERNAL_REF_TYPES)[number];

/** Shape of amp_projects.external_ref. Display only until a mirror exists. */
export type AmpProjectExternalRef = {
  type: AmpProjectExternalRefType;
  id: string;
  url?: string;
};

// What a link can attach. Each sets one target column, which the DB enforces
// through amp_project_links_one_target; an ad or email sets its Library file's.
export const AMP_PROJECT_LINK_KINDS = [
  "opportunity",
  "ad",
  "brief_version",
  "audience",
  "email",
  "action",
  "chat_session",
  "sync",
  "campaign",
  "campaign_artifact",
  "file",
  "action_plan",
  "native_delivery_campaign",
  "external",
] as const;
export type AmpProjectLinkKind = (typeof AMP_PROJECT_LINK_KINDS)[number];

/**
 * Shape of amp_project_links.external: an object outside Hightouch attached
 * to a project. system is where it lives (a destination type such as
 * "facebook", or anything else such as "google-drive"); kind and id identify
 * it there. metadata is the escape hatch for whatever else the writer knows.
 * The action ledger's createdRefs write the same shape.
 */
export type AmpExternalRef = {
  system: string;
  kind: string;
  id: string;
  label: string;
  href?: string;
  metadata?: Record<string, unknown>;
};

// An action is one agent run against destinations.
export const AMP_ACTION_STATUSES = [
  "queued",
  "running",
  "waiting_consent",
  "done",
  "failed",
  "canceled",
] as const;
export type AmpActionStatus = (typeof AMP_ACTION_STATUSES)[number];

export function isAmpActionStatus(value: string): value is AmpActionStatus {
  return (AMP_ACTION_STATUSES as readonly string[]).includes(value);
}

export const AMP_ACTION_CALL_STATUSES = [
  "done",
  "failed",
  "waiting_consent",
  "skipped",
  "dry_run",
] as const;
export type AmpActionCallStatus = (typeof AMP_ACTION_CALL_STATUSES)[number];

export function isAmpActionCallStatus(
  value: string,
): value is AmpActionCallStatus {
  return (AMP_ACTION_CALL_STATUSES as readonly string[]).includes(value);
}

export const AMP_DESTINATION_ACCESS = ["read", "write"] as const;
export type AmpDestinationAccess = (typeof AMP_DESTINATION_ACCESS)[number];

/** Shape of amp_actions.scope: what a run may see and touch. */
export type AmpActionScope = {
  destinations?: {
    id: number;
    name: string;
    type: string;
    access: AmpDestinationAccess;
  }[];
  contextLinkIds?: string[];
  dryRun?: boolean;
};

/** Consent classes a call can fall under. Reads never need consent. */
export const AMP_CONSENT_CLASSES = [
  "spend",
  "send",
  "publish",
  "delete",
  "paused_create",
  "hightouch_write",
] as const;
export type AmpConsentClass = (typeof AMP_CONSENT_CLASSES)[number];

/**
 * Shape of amp_action_calls.payload. The tool layer writes it; the run view
 * reads it. Secrets are redacted before the row is written.
 */
export type AmpActionCallPayload = {
  tool: string;
  /** "hightouch" for resource tools, otherwise the destination type. */
  system: string;
  summary?: string;
  note?: string;
  method?: string;
  path?: string;
  request?: unknown;
  response?: unknown;
  consent?: {
    class: AmpConsentClass;
    decision?: "approved" | "skipped";
    decidedByUserId?: number;
    decidedAt?: string;
  };
  createdRefs?: { kind: string; id: string; label: string; href?: string }[];
  undo?: string;
  durationMs?: number;
};

/**
 * Custom status names mean nothing to code, so each workflow status maps to
 * one of these. Filters, agents and reports read it.
 */
export const AMP_WORKFLOW_STATUS_CATEGORIES = [
  "backlog",
  "active",
  "review",
  "done",
] as const;
export type AmpWorkflowStatusCategory =
  (typeof AMP_WORKFLOW_STATUS_CATEGORIES)[number];

export const AmpWorkflowStatusCategorySchema = z.enum(
  AMP_WORKFLOW_STATUS_CATEGORIES,
);

/** A subtask has no workflow and no review step, so it moves through these alone. */
export const AMP_SUBTASK_STATUS_CATEGORIES = [
  "backlog",
  "active",
  "done",
] as const satisfies readonly AmpWorkflowStatusCategory[];

export function isAmpSubtaskStatusCategory(
  value: string,
): value is (typeof AMP_SUBTASK_STATUS_CATEGORIES)[number] {
  return (AMP_SUBTASK_STATUS_CATEGORIES as readonly string[]).includes(value);
}

export const AMP_DEFAULT_WORKFLOW_NAME = "Default";

/** The statuses of the workflow a workspace gets when it has no default. */
export const AMP_DEFAULT_WORKFLOW_STATUSES: readonly {
  name: string;
  category: AmpWorkflowStatusCategory;
}[] = [
  { name: "To do", category: "backlog" },
  { name: "In progress", category: "active" },
  { name: "In review", category: "review" },
  { name: "Done", category: "done" },
];

export const CREATIVE_REVIEW_NAME = "Creative review";
export const LAUNCHED_STATUS_NAME = "Launched";
// The canvas shortcut and the ad migration must create the same review workflow.
export const CREATIVE_REVIEW_STATUSES = [
  { name: "In review", category: "review", position: 0 },
  { name: "Approved", category: "done", position: 1 },
  { name: LAUNCHED_STATUS_NAME, category: "done", position: 2 },
] satisfies {
  name: string;
  category: AmpWorkflowStatusCategory;
  position: number;
}[];

/** Who a user or group is to a task: responsible for it, doing it, or reviewing it. */
export const AMP_TASK_USER_TYPES = ["owner", "assignee", "reviewer"] as const;
export type AmpTaskUserType = (typeof AMP_TASK_USER_TYPES)[number];

export const AMP_APPROVAL_RULES = ["all", "any"] as const;
export type AmpApprovalRule = (typeof AMP_APPROVAL_RULES)[number];

/**
 * A member's latest non-comment review is their state; requested means asked
 * again, and withdrawn takes their review back.
 */
export const AMP_TASK_REVIEW_KINDS = [
  "approved",
  "changes_requested",
  "commented",
  "requested",
  "withdrawn",
] as const;
export type AmpTaskReviewKind = (typeof AMP_TASK_REVIEW_KINDS)[number];

/** The type of an amp_task_relations row whose source task blocks its target. */
export const AMP_TASK_BLOCKS_RELATION = "blocks";

/** Who a user is to a project: responsible for it, or working on it. */
export const AMP_PROJECT_USER_TYPES = ["owner", "member"] as const;
export type AmpProjectUserType = (typeof AMP_PROJECT_USER_TYPES)[number];

/**
 * The colors a status or tag takes on the board, stored as the color token. Here
 * so the app's color picker and the agent's tag tools write the same values.
 */
export const AMP_BOARD_COLORS = {
  gray: "gray.500",
  blue: "blue.500",
  green: "green.500",
  yellow: "yellow.500",
  orange: "orange.500",
  red: "red.500",
  purple: "purple.500",
  pink: "pink.500",
} as const;
export type AmpBoardColorName = keyof typeof AMP_BOARD_COLORS;
export const AMP_BOARD_COLOR_NAMES = Object.keys(AMP_BOARD_COLORS) as [
  AmpBoardColorName,
  ...AmpBoardColorName[],
];
